/**
 * Leagues — the baselines, and how much data each one rests on.
 *
 * This screen exists because of the phantom edge. The model was anchoring on a generic ~2.9-goal
 * soccer prior while MLS runs ~3.1, and **a 0.4-goal error moves the Under 3.5 probability by ~9
 * points** — the same size as the "edge" being reported. Fourteen of fifteen MLS matches showed a
 * phantom Under edge: one systematic error repeated, not fourteen opportunities.
 *
 * So the sample size is shown as prominently as the numbers. **Never trust a sub-100-match league
 * prior**: a 20-match Polymarket sample put MLS at 3.20 goals and 45% Under 3.5, while 779 ESPN
 * matches put it at 3.10 and 63%.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { api, describeError } from '@/api';
import { mappedCount } from '@/data/teamCodes';
import { isReliable, rateMarginPoints } from '@/stats/leagueStats';
import { Empty } from '@/screens/Upcoming';
import { useStore } from '@/store';
import { useLayout } from '@/useLayout';
import * as React from 'react';
import { ScrollView, View } from 'react-native';

export function LeaguesScreen() {
  const { wide } = useLayout();
  const { leagues, scorecards, load } = useStore();
  const [busy, setBusy] = React.useState<string | null>(null);
  const [note, setNote] = React.useState('');

  const total = leagues.reduce((a, l) => a + (l.stats?.matches ?? 0), 0);

  const backfill = async (code: string) => {
    setBusy(code);
    setNote(`Pulling ESPN history for ${code}…`);
    try {
      const r = await api.post<{ fetched: number; added: number }>(
        `/api/results/backfill?league=${encodeURIComponent(code)}`,
      );
      setNote(`${code}: ${r.fetched} fetched, ${r.added} new.`);
      await load();
    } catch (e) {
      setNote(describeError(e));
    } finally {
      setBusy(null);
    }
  };

  if (leagues.length === 0) {
    return <Empty title="No baselines loaded" body="Pair the app in Settings, then pull to reload." />;
  }

  return (
    <ScrollView contentContainerClassName="gap-3 pb-6">
      <Text variant="small" className="text-muted-foreground">
        {total.toLocaleString()} completed matches on record, feeding the prediction prompt
        {note ? ` · ${note}` : ''}
      </Text>

      <View className={cn('gap-3', wide && 'flex-row flex-wrap')}>
        {leagues
          .slice()
          .sort((a, b) => (b.stats?.matches ?? 0) - (a.stats?.matches ?? 0))
          .map((l) => {
            const s = l.stats;
            const n = s?.matches ?? 0;
            const cards = scorecards.filter((c) => c.league_code === l.code);
            return (
              <View key={l.code} className={cn(wide && 'min-w-[320px] flex-1 basis-[320px]')}>
                <Card>
                  <CardHeader className="gap-1">
                    <View className="flex-row items-start justify-between gap-2">
                      <Text variant="heading" numberOfLines={1} className="flex-1">
                        {l.displayName}
                      </Text>
                      <Badge variant={n === 0 ? 'outline' : isReliable(s!) ? 'success' : 'warning'}>
                        <Text>{n} matches</Text>
                      </Badge>
                    </View>
                    {n > 0 && n < 100 ? (
                      <Text variant="caption" className="text-warning">
                        Under 100 matches — do not trust this prior. A 20-match sample once put MLS
                        at 45% Under 3.5 when the real figure was 63%.
                      </Text>
                    ) : null}
                  </CardHeader>

                  <CardContent className="gap-1 py-3">
                    {n === 0 ? (
                      <Text variant="small" className="text-muted-foreground">
                        No results stored. Backfill pulls two seasons from ESPN.
                      </Text>
                    ) : (
                      <>
                        <Text variant="small">
                          {s!.avgTotalGoals.toFixed(2)} goals/match ({s!.avgHomeGoals.toFixed(2)} home,{' '}
                          {s!.avgAwayGoals.toFixed(2)} away)
                        </Text>
                        <Text variant="small" className="text-muted-foreground">
                          BTTS {s!.bttsRate.toFixed(0)}% · O2.5 {s!.over25Rate.toFixed(0)}% · U3.5{' '}
                          {s!.under35Rate.toFixed(0)}%
                        </Text>
                        <Text variant="small" className="text-muted-foreground">
                          H {s!.homeWinRate.toFixed(0)}% · D {s!.drawRate.toFixed(0)}% · A{' '}
                          {s!.awayWinRate.toFixed(0)}%  ·  ±{rateMarginPoints(s!).toFixed(0)} pts
                        </Text>
                        <Text variant="caption" className="text-muted-foreground">
                          {cards.length === 0
                            ? 'Model unscored — run a backtest to grade this league.'
                            : cards
                                .map(
                                  (c) =>
                                    `${c.signal.replace(/_.*/, '')} ${c.edge_points >= 0 ? '+' : ''}${c.edge_points.toFixed(1)}`,
                                )
                                .join('  ')}
                        </Text>
                        <Text variant="caption" className="text-muted-foreground">
                          {mappedCount(l.code)} team codes mapped — a fixture with either side
                          unmapped gets no propensity signal rather than a guessed one.
                        </Text>
                      </>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1 self-start"
                      disabled={busy !== null}
                      onPress={() => void backfill(l.code)}>
                      <Text>{busy === l.code ? 'Backfilling…' : 'Backfill from ESPN'}</Text>
                    </Button>
                  </CardContent>
                </Card>
              </View>
            );
          })}
      </View>
    </ScrollView>
  );
}
